import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { Smiley, SmileyMeh, SmileySad } from "@phosphor-icons/react";

const RATINGS = [
  { key: "sleep", label: "Sleep" },
  { key: "energy", label: "Energy" },
  { key: "libido", label: "Libido" },
  { key: "water_intake", label: "Water" },
  { key: "physical_activity", label: "Activity" },
  { key: "body_image", label: "Body image" },
  { key: "stress_levels", label: "Stress" },
];

const moodIcon = (m) => m === "happy" ? Smiley : m === "sad" ? SmileySad : SmileyMeh;

export default function ClientVitalsView({ clientId }) {
  const [profile, setProfile] = useState(null);
  const [body, setBody] = useState([]);
  const [wellness, setWellness] = useState([]);
  const [err, setErr] = useState("");
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    (async () => {
      try {
        const [p, b, w] = await Promise.all([
          http.get(`/coach/clients/${clientId}/profile`),
          http.get(`/coach/clients/${clientId}/body-stats`),
          http.get(`/coach/clients/${clientId}/wellness`),
        ]);
        setProfile(p.data); setBody(b.data); setWellness(w.data);
      } catch (e) { setErr(apiError(e)); }
    })();
  }, [clientId]);

  const latestBody = body[body.length - 1];
  const latestWell = wellness[wellness.length - 1];
  const MoodIcon = latestWell ? moodIcon(latestWell.mood) : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="coach-vitals-view">
      <div className="id-card">
        <div className="id-label mb-3">Profile</div>
        {!profile ? <div className="text-zinc-500 text-sm">Loading...</div> : (
          <div className="grid grid-cols-3 gap-2 text-sm font-manrope">
            <Stat label="Height" value={profile.height_cm ? `${profile.height_cm} cm` : "—"} />
            <Stat label="Sex" value={profile.sex || "—"} />
            <Stat label="Occupation" value={profile.occupation || "—"} />
          </div>
        )}
        {profile?.starting_photos?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#262626]">
            <div className="id-label mb-2">Starting photos</div>
            <div className="grid grid-cols-3 gap-2" data-testid="coach-starting-photos">
              {profile.starting_photos.map((p) => (
                <div key={p.id} className="aspect-[3/4] border border-[#262626] relative overflow-hidden bg-[#0F0F0F]">
                  <img src={`${apiBase}/api/client/profile/photos/${p.id}`} alt={p.label} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute top-1 left-1 id-label text-zinc-200 bg-[#0A0A0A]/80 px-1 py-0.5 text-[9px]">{p.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="id-card">
        <div className="id-label mb-3">Latest body stats</div>
        {!latestBody ? <div className="text-zinc-500 text-sm">No check-ins yet.</div> : (
          <>
            <div className="text-xs text-zinc-500 font-manrope mb-2">{(latestBody.date || "").slice(0, 10)}</div>
            <div className="grid grid-cols-4 gap-2 text-sm font-manrope">
              <Stat label="Wt" value={`${latestBody.weight_kg} kg`} />
              <Stat label="Waist" value={`${latestBody.waist_cm} cm`} />
              <Stat label="Leg" value={`${latestBody.upper_leg_cm} cm`} />
              <Stat label="Arm" value={`${latestBody.arm_cm} cm`} />
            </div>
            <div className="text-zinc-500 text-xs mt-3 font-manrope">{body.length} total entries</div>
          </>
        )}
      </div>

      <div className="id-card lg:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <div className="id-label">Latest wellness</div>
          {latestWell && MoodIcon && (
            <div className="flex items-center gap-2 id-label text-[#FF3333]">
              <MoodIcon size={20} weight="fill" /> {latestWell.mood}
            </div>
          )}
        </div>
        {!latestWell ? <div className="text-zinc-500 text-sm">No wellness rating yet.</div> : (
          <>
            <div className="text-xs text-zinc-500 font-manrope mb-3">{(latestWell.date || "").slice(0, 10)}</div>
            <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
              {RATINGS.map((r) => (
                <div key={r.key} className="border border-[#262626] px-2 py-2 text-center" data-testid={`coach-well-${r.key}`}>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-[0.15em]">{r.label}</div>
                  <div className="font-oswald text-2xl font-bold">{latestWell[r.key]}</div>
                </div>
              ))}
            </div>
            <div className="text-zinc-500 text-xs mt-3 font-manrope">{wellness.length} total entries</div>
          </>
        )}
      </div>

      {err && <div className="lg:col-span-2 border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 text-xs text-[#FF3333]">{err}</div>}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] text-zinc-500 uppercase tracking-[0.15em] font-bold">{label}</div>
      <div className="text-zinc-100 font-manrope">{value}</div>
    </div>
  );
}
